/**
 * Top navbar shell for Doowii Dossier — logo, primary nav, and a tiny
 * inline user/logout dropdown driven by /whoami directly.
 */
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import doowiiMarkFullColor from './assets/doowii-mark-full-color.png';
import '../../../styles/nav-bar.css';

const DJ_URL = process.env.REACT_APP_DJ_URL || '';

interface CurrentUser {
  username?: string;
  email?: string;
  name?: string;
  is_admin?: boolean;
}

async function fetchWhoami(): Promise<CurrentUser | null> {
  try {
    const res = await fetch(`${DJ_URL}/whoami/`, { credentials: 'include' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function logout(): Promise<void> {
  try {
    await fetch(`${DJ_URL}/logout/`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    /* no-op */
  }
  window.location.href = '/';
}

function UserMenu() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchWhoami().then(setUser);
  }, []);

  const display = user?.name || user?.username || user?.email || 'User';

  return (
    <div className="user-menu">
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen(o => !o)}
      >
        {display}
      </button>
      {open && (
        <div className="user-menu-dropdown">
          {user?.email && <div className="user-menu-email">{user.email}</div>}
          {user?.is_admin && (
            <>
              <a href="/settings" className="user-menu-item">
                Settings
              </a>
              <a href="/tenants" className="user-menu-item">
                Tenants
              </a>
            </>
          )}
          <button type="button" className="user-menu-logout" onClick={logout}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function Root() {
  return (
    <>
      <Helmet>
        <title>Doowii Dossier</title>
        <meta
          name="description"
          content="Doowii Dossier — Malloy + dbt + data quality"
        />
      </Helmet>
      <div className="container d-flex align-items-center justify-content-between">
        <div className="header">
          <div className="logo">
            <a href="/" className="brand-link">
              <img
                src={doowiiMarkFullColor}
                alt="Doowii"
                className="brand-mark"
              />
              <h2 className="brand-title">
                <span className="brand-doowii">doowii</span> <b>Dossier</b>
              </h2>
            </a>
          </div>
          <div className="menu">
            <div className="menu-item here menu-here-bg menu-lg-down-accordion me-0 me-lg-2 fw-semibold">
              <span className="menu-link">
                <span className="menu-title">
                  <a href="/">Catalog</a>
                </span>
              </span>
              <span className="menu-link">
                <span className="menu-title">
                  <a href="/build">Build</a>
                </span>
              </span>
              <span className="menu-link">
                <span className="menu-title">
                  <a href="/sentinel">Sentinel</a>
                </span>
              </span>
              <span className="menu-link">
                <span className="menu-title">
                  <a href="/health">Models</a>
                </span>
              </span>
              <span className="menu-link">
                <span className="menu-title">
                  <a href="/langsmith">LangSmith</a>
                </span>
              </span>
              <span className="menu-link">
                <span className="menu-title">
                  <a href="/mage">Mage</a>
                </span>
              </span>
            </div>
          </div>
        </div>
        {process.env.REACT_DISABLE_AUTH === 'true' ? null : (
          <div className="nav-right">
            <UserMenu />
          </div>
        )}
      </div>
      <Outlet />
    </>
  );
}
